import React, { useEffect, useState, useRef } from "react";
import axiosInstance from "../utils/axios";
import Icon from "./AppIcon";

const SessionTimeoutManager = () => {
  const [warningSeconds, setWarningSeconds] = useState(null);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const timeoutRef = useRef(30); // Default 30 minutes

  // Load configured timeout from server / storage
  useEffect(() => {
    const initTimeout = async () => {
      const stored = sessionStorage.getItem("session_timeout");
      if (stored && parseInt(stored, 10) > 0) {
        timeoutRef.current = parseInt(stored, 10);
      }

      const token = sessionStorage.getItem("token");
      if (!token) return;

      try {
        const response = await axiosInstance.get("/general-settings");
        const serverTimeout = response?.data?.data?.session_timeout;
        if (serverTimeout && parseInt(serverTimeout, 10) > 0) {
          const mins = parseInt(serverTimeout, 10);
          timeoutRef.current = mins;
          sessionStorage.setItem("session_timeout", String(mins));
        }
      } catch (e) {
        // Fallback to existing
      }
    };

    initTimeout();

    // Listen for live updates from GeneralSettingsTab
    const handleTimeoutUpdated = (e) => {
      const newMinutes = e.detail || sessionStorage.getItem("session_timeout");
      if (newMinutes && parseInt(newMinutes, 10) > 0) {
        timeoutRef.current = parseInt(newMinutes, 10);
        sessionStorage.setItem("last_activity_time", String(Date.now()));
        setShowWarningModal(false);
        setWarningSeconds(null);
      }
    };

    window.addEventListener("session-timeout-updated", handleTimeoutUpdated);
    return () => {
      window.removeEventListener("session-timeout-updated", handleTimeoutUpdated);
    };
  }, []);

  // Track user activity & enforce inactivity timeout
  useEffect(() => {
    const token = sessionStorage.getItem("token");
    if (!token) return;

    // Initialize last activity time if not set
    if (!sessionStorage.getItem("last_activity_time")) {
      sessionStorage.setItem("last_activity_time", String(Date.now()));
    }

    let lastRecordedActivity = Date.now();

    // User activity handler (throttled to once every 2 seconds)
    const recordActivity = () => {
      const now = Date.now();
      if (now - lastRecordedActivity > 2000) {
        lastRecordedActivity = now;
        sessionStorage.setItem("last_activity_time", String(now));
        if (showWarningModal) {
          setShowWarningModal(false);
          setWarningSeconds(null);
        }
      }
    };

    const activityEvents = [
      "mousedown",
      "mousemove",
      "keydown",
      "scroll",
      "touchstart",
      "click"
    ];

    activityEvents.forEach((evt) => {
      window.addEventListener(evt, recordActivity, { passive: true });
    });

    // Inactivity check tick (runs every 3 seconds)
    const checkInterval = setInterval(() => {
      const currentToken = sessionStorage.getItem("token");
      if (!currentToken) return;

      const lastActivity = parseInt(
        sessionStorage.getItem("last_activity_time") || String(Date.now()),
        10
      );

      const timeoutMinutes = timeoutRef.current || 30;
      const timeoutMs = timeoutMinutes * 60 * 1000;
      const idleMs = Date.now() - lastActivity;
      const remainingMs = timeoutMs - idleMs;

      // 1. TIMED OUT: Log out immediately
      if (idleMs >= timeoutMs) {
        sessionStorage.removeItem("token");
        sessionStorage.removeItem("user");
        sessionStorage.removeItem("last_activity_time");
        clearInterval(checkInterval);
        window.location.href = "/login?timeout=1";
        return;
      }

      // 2. WARNING STAGE: 45 seconds or less remaining
      if (remainingMs <= 45000 && remainingMs > 0) {
        const remainingSec = Math.max(1, Math.ceil(remainingMs / 1000));
        setWarningSeconds(remainingSec);
        setShowWarningModal(true);
      } else {
        if (showWarningModal) {
          setShowWarningModal(false);
          setWarningSeconds(null);
        }
      }
    }, 3000);

    return () => {
      activityEvents.forEach((evt) => {
        window.removeEventListener(evt, recordActivity);
      });
      clearInterval(checkInterval);
    };
  }, [showWarningModal]);

  const handleStaySignedIn = () => {
    sessionStorage.setItem("last_activity_time", String(Date.now()));
    setShowWarningModal(false);
    setWarningSeconds(null);
  };

  const handleLogoutNow = () => {
    sessionStorage.removeItem("token");
    sessionStorage.removeItem("user");
    sessionStorage.removeItem("last_activity_time");
    window.location.href = "/login?timeout=1";
  };

  if (!showWarningModal || !sessionStorage.getItem("token")) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in">
      <div className="bg-card border border-border rounded-2xl p-6 max-w-md w-full shadow-2xl text-center space-y-4">
        <div className="w-14 h-14 rounded-2xl bg-amber-100 text-amber-600 dark:bg-amber-950/60 dark:text-amber-400 flex items-center justify-center mx-auto">
          <Icon name="Clock" size={28} />
        </div>

        <div>
          <h3 className="text-lg font-bold text-card-foreground">
            Session Timeout Warning
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            You have been inactive. For your security, you will be automatically logged out in:
          </p>
        </div>

        <div className="inline-block px-4 py-2 bg-amber-50 dark:bg-amber-950/80 border border-amber-300 dark:border-amber-700 rounded-xl font-mono text-2xl font-bold text-amber-700 dark:text-amber-300">
          {warningSeconds}s
        </div>

        <div className="flex items-center justify-center gap-3 pt-2">
          <button
            type="button"
            onClick={handleLogoutNow}
            className="px-4 py-2 text-xs font-semibold rounded-xl border border-border text-muted-foreground hover:bg-muted transition-colors"
          >
            Log Out Now
          </button>
          <button
            type="button"
            onClick={handleStaySignedIn}
            className="px-5 py-2 text-xs font-semibold rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm transition-all"
          >
            Stay Signed In
          </button>
        </div>
      </div>
    </div>
  );
};

export default SessionTimeoutManager;

